import {
  SHIFT_ASSIGNMENT_TARGET_LABELS,
  SHIFT_CREDIT_SALE_KIND_LABELS,
  SHIFT_TOTALIZER_SOURCE_LABELS,
  STAFF_DUTY_LABELS,
  type Shift,
} from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  MoneyText,
  SkeletonRows,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeftRight,
  Ban,
  Banknote,
  Check,
  Coins,
  Droplet,
  Lock,
  Plus,
  Receipt,
  Square,
  Undo2,
  UserPlus,
} from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  SimpleTable,
  tdClass,
  tdRightClass,
  thClass,
  thRightClass,
} from "../../../../../features/counterparties/shared.js";
import {
  ShiftActionDialog,
  type ShiftClosingAction,
  StartClosingDialog,
} from "../../../../../features/shifts/closing-dialogs.js";
import { ClosingsTab } from "../../../../../features/shifts/closings-tab.js";
import {
  AddAssignmentDialog,
  AddMemberDialog,
  HandoverDialog,
} from "../../../../../features/shifts/member-dialogs.js";
import {
  useShift,
  useShiftCashDrops,
  useShiftCashExpenses,
  useShiftCreditSales,
  useShiftPermissions,
  useShiftTestDraws,
} from "../../../../../features/shifts/queries.js";
import {
  CancelDialog,
  type CancelTarget,
  CashDropDialog,
  CashExpenseDialog,
  CreditSaleDialog,
  TestDrawDialog,
} from "../../../../../features/shifts/record-dialogs.js";
import { Field, ShiftStatusBadge, ShiftsPage } from "../../../../../features/shifts/shared.js";
import { ShiftReportTab } from "../../../../../features/shifts/shift-report-tab.js";
import { formatLitres } from "../../../../../features/shifts/shift-values.js";
import { errorMessage } from "../../../../../lib/errors.js";
import { formatDate, formatDateTime } from "../../../../../lib/format.js";

/**
 * Vardiya ayrıntısının sekmeleri. "Hesaplar" (pompacı / kasiyer kapanışı) 2. aşamada eklendi.
 * İçerik MUTLAKA `TabsContent` içinde olmalıdır; aksi hâlde tetikleyicinin `aria-controls` hedefi yoktur
 * ve axe `aria-valid-attr-value` verir.
 */
const SHIFT_TABS = [
  "members",
  "closings",
  "cashDrops",
  "cashExpenses",
  "creditSales",
  "testDraws",
  "report",
] as const;
type ShiftTab = (typeof SHIFT_TABS)[number];

const searchSchema = z.object({
  tab: z.enum(SHIFT_TABS).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/vardiya/vardiyalar/$id/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: ShiftDetailPage,
});

function ShiftDetailPage(): ReactElement {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const permissions = useShiftPermissions();
  const shift = useShift(id);
  const title = shift.data?.shiftNo ?? t("shifts.detail.title");

  return (
    <ShiftsPage
      title={title}
      breadcrumb={[
        {
          label: t("menu.shifts"),
          link: (children, className) => (
            <Link to="/vardiya/vardiyalar" className={className}>
              {children}
            </Link>
          ),
        },
        { label: title },
      ]}
    >
      {() =>
        !permissions.canRead ? (
          <Alert tone="info">{t("shifts.noPermission")}</Alert>
        ) : shift.error ? (
          <Alert tone="danger">{errorMessage(shift.error)}</Alert>
        ) : !shift.data ? (
          <SkeletonRows rows={8} />
        ) : (
          <ShiftDetail shift={shift.data as Shift} />
        )
      }
    </ShiftsPage>
  );
}

function ShiftDetail({ shift }: { shift: Shift }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const permissions = useShiftPermissions();
  const tab: ShiftTab = search.tab ?? "members";
  const [dialog, setDialog] = useState<
    | "member"
    | "assignment"
    | "handover"
    | "cashDrop"
    | "cashExpense"
    | "creditSale"
    | "testDraw"
    | "startClosing"
    | null
  >(null);
  const [cancelTarget, setCancelTarget] = useState<CancelTarget | null>(null);
  const [shiftAction, setShiftAction] = useState<ShiftClosingAction | null>(null);

  const writable = shift.status === "OPEN" || shift.status === "CLOSING";
  const canWrite = writable && permissions.canManage && !shift.businessDayLocked;
  const canClose = permissions.canManageClosing && !shift.businessDayLocked;
  const close = () => setDialog(null);

  return (
    <div className="grid gap-4">
      <Card className="grid gap-3 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-lg font-semibold">{shift.shiftNo}</span>
          <ShiftStatusBadge status={shift.status} />
          {shift.businessDayLocked ? (
            <StatusBadge tone="warning">{t("shifts.detail.dayLocked")}</StatusBadge>
          ) : null}
          <span className="text-sm text-muted-foreground">
            {shift.name} · {shift.stationName}
          </span>
        </div>
        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Field label={t("shifts.fields.businessDate")}>{formatDate(shift.businessDate)}</Field>
          <Field label={t("shifts.fields.openedAt")}>{formatDateTime(shift.openedAt)}</Field>
          <Field label={t("shifts.fields.closedAt")}>
            {formatDateTime(shift.closingStartedAt ?? shift.closedAt)}
          </Field>
          <Field label={t("shifts.fields.openingFloat")}>
            <MoneyText value={shift.totals.openingFloat} />
          </Field>
          <Field label={t("shifts.fields.cashDropTotal")}>
            <MoneyText value={shift.totals.cashDropTotal} />
          </Field>
          <Field label={t("shifts.fields.cashExpenseTotal")}>
            <MoneyText value={shift.totals.cashExpenseTotal} />
          </Field>
          <Field label={t("shifts.fields.creditSaleTotal")}>
            <MoneyText value={shift.totals.creditSaleTotal} />
          </Field>
          <Field label={t("shifts.fields.testDrawLitres")}>{formatLitres(shift.totals.testDrawLitres)}</Field>
        </dl>
        {shift.previousUnclosedReason ? (
          <Alert tone="warning" title={t("shifts.detail.previousUnclosedTitle")}>
            {shift.previousUnclosedReason}
          </Alert>
        ) : null}
        {shift.cancelReason ? (
          <Alert tone="warning" title={t("shifts.detail.cancelledTitle")}>
            {shift.cancelReason}
          </Alert>
        ) : null}
        {shift.note ? <p className="text-sm text-muted-foreground">{shift.note}</p> : null}

        {/* Kapanış akışı: aç → kapanışı başlat → hesaplar → kapat → onayla. Her düğme yalnızca durumu
            uygunken ve yetki varken çizilir (sunucu da aynı geçişi arar). */}
        <div className="flex flex-wrap gap-2">
          {shift.status === "OPEN" && canClose ? (
            <Button size="sm" onClick={() => setDialog("startClosing")}>
              <Square aria-hidden="true" />
              {t("shifts.closing.startTitle")}
            </Button>
          ) : null}
          {shift.status === "CLOSING" && canClose ? (
            <>
              <Button size="sm" onClick={() => setShiftAction("close")}>
                <Lock aria-hidden="true" />
                {t("shifts.closing.action.close.title")}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShiftAction("reopenShift")}>
                <Undo2 aria-hidden="true" />
                {t("shifts.closing.action.reopenShift.title")}
              </Button>
            </>
          ) : null}
          {shift.status === "CLOSED" && permissions.canApprove && !shift.businessDayLocked ? (
            <Button size="sm" onClick={() => setShiftAction("approve")}>
              <Check aria-hidden="true" />
              {t("shifts.closing.action.approve.title")}
            </Button>
          ) : null}
          {shift.status === "APPROVED" && permissions.canApprove && !shift.businessDayLocked ? (
            <Button variant="outline" size="sm" onClick={() => setShiftAction("revokeApproval")}>
              <Undo2 aria-hidden="true" />
              {t("shifts.closing.action.revokeApproval.title")}
            </Button>
          ) : null}
          {shift.status === "OPEN" && permissions.canManage ? (
            <>
              <Button variant="outline" size="sm" onClick={() => setCancelTarget({ kind: "shift" })}>
                <Ban aria-hidden="true" />
                {t("shifts.records.cancelShiftTitle")}
              </Button>
              <span className="self-center text-xs text-muted-foreground">
                {t("shifts.detail.cancelHint")}
              </span>
            </>
          ) : null}
        </div>
      </Card>

      <Tabs
        className="grid gap-4"
        value={tab}
        onValueChange={(next) =>
          void navigate({
            to: ".",
            search: ((previous: Record<string, unknown>) => ({ ...previous, tab: next })) as never,
            replace: true,
          })
        }
      >
        <TabsList aria-label={t("shifts.detail.tabs")}>
          {SHIFT_TABS.map((value) => (
            <TabsTrigger key={value} value={value}>
              {t(`shifts.detail.tab.${value}`)}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Etkin sekmenin paneli: tetikleyicinin `aria-controls` hedefi var olmalı (axe: aria-valid-attr-value). */}
        <TabsContent value={tab} className="grid gap-4 pt-0">
          {tab === "members" ? (
            <MembersTab shift={shift} canWrite={canWrite} onOpenDialog={setDialog} />
          ) : null}
          {tab === "closings" ? <ClosingsTab shift={shift} /> : null}
          {tab === "cashDrops" ? (
            <CashDropsTab
              shift={shift}
              canWrite={canWrite}
              onAdd={() => setDialog("cashDrop")}
              onCancel={setCancelTarget}
            />
          ) : null}
          {tab === "cashExpenses" ? (
            <CashExpensesTab shift={shift} canWrite={canWrite} onAdd={() => setDialog("cashExpense")} />
          ) : null}
          {tab === "creditSales" ? (
            <CreditSalesTab
              shift={shift}
              canWrite={canWrite}
              onAdd={() => setDialog("creditSale")}
              onCancel={setCancelTarget}
            />
          ) : null}
          {tab === "testDraws" ? (
            <TestDrawsTab shift={shift} canWrite={canWrite} onAdd={() => setDialog("testDraw")} />
          ) : null}
          {tab === "report" ? <ShiftReportTab shift={shift} /> : null}
        </TabsContent>
      </Tabs>

      <AddMemberDialog open={dialog === "member"} onOpenChange={close} shift={shift} />
      <AddAssignmentDialog open={dialog === "assignment"} onOpenChange={close} shift={shift} />
      <HandoverDialog open={dialog === "handover"} onOpenChange={close} shift={shift} />
      <CashDropDialog
        open={dialog === "cashDrop"}
        onOpenChange={close}
        shiftId={shift.id}
        members={shift.members}
      />
      <CashExpenseDialog
        open={dialog === "cashExpense"}
        onOpenChange={close}
        shiftId={shift.id}
        members={shift.members}
        companyId={shift.companyId}
      />
      <CreditSaleDialog
        open={dialog === "creditSale"}
        onOpenChange={close}
        shiftId={shift.id}
        members={shift.members}
        stationId={shift.stationId}
      />
      <TestDrawDialog
        open={dialog === "testDraw"}
        onOpenChange={close}
        shiftId={shift.id}
        members={shift.members}
        stationId={shift.stationId}
      />
      <CancelDialog target={cancelTarget} shiftId={shift.id} onClose={() => setCancelTarget(null)} />
      <StartClosingDialog open={dialog === "startClosing"} onOpenChange={close} shift={shift} />
      <ShiftActionDialog action={shiftAction} shiftId={shift.id} onClose={() => setShiftAction(null)} />
    </div>
  );
}

function MembersTab({
  shift,
  canWrite,
  onOpenDialog,
}: {
  shift: Shift;
  canWrite: boolean;
  onOpenDialog: (dialog: "member" | "assignment" | "handover") => void;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <div className="grid gap-4">
      {canWrite ? (
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => onOpenDialog("member")}>
            <UserPlus aria-hidden="true" />
            {t("shifts.members.addTitle")}
          </Button>
          <Button variant="outline" size="sm" onClick={() => onOpenDialog("assignment")}>
            <Plus aria-hidden="true" />
            {t("shifts.members.assignmentTitle")}
          </Button>
          <Button variant="outline" size="sm" onClick={() => onOpenDialog("handover")}>
            <ArrowLeftRight aria-hidden="true" />
            {t("shifts.members.handoverTitle")}
          </Button>
        </div>
      ) : null}

      <SimpleTable
        caption={t("shifts.detail.membersCaption")}
        head={
          <tr>
            <th scope="col" className={thClass}>
              {t("shifts.fields.staff")}
            </th>
            <th scope="col" className={thClass}>
              {t("shifts.open.duty")}
            </th>
            <th scope="col" className={thClass}>
              {t("shifts.fields.cashRegister")}
            </th>
            <th scope="col" className={thRightClass}>
              {t("shifts.fields.openingFloat")}
            </th>
            <th scope="col" className={thRightClass}>
              {t("shifts.fields.cashDropTotal")}
            </th>
            <th scope="col" className={thRightClass}>
              {t("shifts.fields.creditSaleTotal")}
            </th>
          </tr>
        }
      >
        {shift.members.map((member) => (
          <tr key={member.id} className="border-t align-top">
            <td className={tdClass}>
              {member.staffCode} — {member.staffName}
              {member.counterpartyId === null ? (
                <>
                  <br />
                  <span className="text-xs text-muted-foreground">
                    {t("shifts.staff.noCounterpartyShort")}
                  </span>
                </>
              ) : null}
            </td>
            <td className={tdClass}>{STAFF_DUTY_LABELS[member.duty]}</td>
            <td className={tdClass}>{member.cashRegisterName ?? "—"}</td>
            <td className={tdRightClass}>
              <MoneyText value={member.openingFloat} />
            </td>
            <td className={tdRightClass}>
              <MoneyText value={member.cashDropTotal} />
            </td>
            <td className={tdRightClass}>
              <MoneyText value={member.creditSaleTotal} />
            </td>
          </tr>
        ))}
      </SimpleTable>

      <SimpleTable
        caption={t("shifts.detail.assignmentsCaption")}
        head={
          <tr>
            <th scope="col" className={thClass}>
              {t("shifts.fields.target")}
            </th>
            <th scope="col" className={thClass}>
              {t("shifts.fields.staff")}
            </th>
            <th scope="col" className={thClass}>
              {t("shifts.fields.fromAt")}
            </th>
            <th scope="col" className={thClass}>
              {t("shifts.fields.toAt")}
            </th>
            <th scope="col" className={thRightClass}>
              {t("shifts.fields.openingTotalizer")}
            </th>
            <th scope="col" className={thClass}>
              {t("shifts.fields.totalizerSource")}
            </th>
          </tr>
        }
      >
        {shift.assignments.map((assignment) => (
          <tr key={assignment.id} className="border-t align-top">
            <td className={tdClass}>
              {assignment.targetType === "NOZZLE"
                ? (assignment.nozzleLabel ?? SHIFT_ASSIGNMENT_TARGET_LABELS.NOZZLE)
                : (assignment.cashRegisterName ?? SHIFT_ASSIGNMENT_TARGET_LABELS.CASH_REGISTER)}
            </td>
            <td className={tdClass}>{assignment.staffName}</td>
            <td className={`${tdClass} tabular`}>{formatDateTime(assignment.fromAt)}</td>
            <td className={`${tdClass} tabular`}>{formatDateTime(assignment.toAt)}</td>
            <td className={tdRightClass}>{formatLitres(assignment.openingTotalizer)}</td>
            <td className={tdClass}>
              {assignment.openingSource ? (
                <StatusBadge tone={assignment.openingSource === "MANUAL" ? "warning" : "neutral"}>
                  {SHIFT_TOTALIZER_SOURCE_LABELS[assignment.openingSource]}
                </StatusBadge>
              ) : (
                "—"
              )}
              {assignment.openingOverrideReason ? (
                <>
                  <br />
                  <span className="text-xs text-muted-foreground">{assignment.openingOverrideReason}</span>
                </>
              ) : null}
            </td>
          </tr>
        ))}
      </SimpleTable>
    </div>
  );
}

function CashDropsTab({
  shift,
  canWrite,
  onAdd,
  onCancel,
}: {
  shift: Shift;
  canWrite: boolean;
  onAdd: () => void;
  onCancel: (target: CancelTarget) => void;
}): ReactElement {
  const { t } = useTranslation();
  const drops = useShiftCashDrops(shift.id);
  return (
    <TabBody
      isPending={drops.isPending}
      error={drops.error}
      isEmpty={(drops.data ?? []).length === 0}
      emptyTitle={t("shifts.detail.cashDropsEmpty")}
      icon={<Banknote />}
      action={
        canWrite ? (
          <Button variant="outline" size="sm" onClick={onAdd}>
            <Plus aria-hidden="true" />
            {t("shifts.records.cashDropTitle")}
          </Button>
        ) : null
      }
    >
      <SimpleTable
        caption={t("shifts.detail.cashDropsCaption")}
        head={
          <tr>
            <th scope="col" className={thClass}>
              {t("shifts.fields.staff")}
            </th>
            <th scope="col" className={thClass}>
              {t("shifts.fields.droppedAt")}
            </th>
            <th scope="col" className={thClass}>
              {t("shifts.fields.cashRegister")}
            </th>
            <th scope="col" className={thRightClass}>
              {t("shifts.records.amount")}
            </th>
            <th scope="col" className={thClass}>
              {t("common.status")}
            </th>
            <th scope="col" className={thClass}>
              {t("common.actions")}
            </th>
          </tr>
        }
      >
        {(drops.data ?? []).map((drop) => (
          <tr key={drop.id} className="border-t align-top">
            <td className={tdClass}>{drop.staffName}</td>
            <td className={`${tdClass} tabular`}>{formatDateTime(drop.droppedAt)}</td>
            <td className={tdClass}>{drop.cashRegisterName}</td>
            <td className={tdRightClass}>
              <MoneyText value={drop.amount} />
            </td>
            <td className={tdClass}>
              {drop.cancelledAt ? (
                <>
                  <StatusBadge tone="danger">{t("shifts.records.cancelledBadge")}</StatusBadge>
                  {drop.cancelReason ? (
                    <>
                      <br />
                      <span className="text-xs text-muted-foreground">{drop.cancelReason}</span>
                    </>
                  ) : null}
                </>
              ) : (
                <StatusBadge tone="success">{t("shifts.records.activeBadge")}</StatusBadge>
              )}
            </td>
            <td className={tdClass}>
              {canWrite && !drop.cancelledAt ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onCancel({ kind: "cashDrop", recordId: drop.id })}
                >
                  {t("shifts.records.cancelRecord")}
                </Button>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </td>
          </tr>
        ))}
      </SimpleTable>
    </TabBody>
  );
}

function CashExpensesTab({
  shift,
  canWrite,
  onAdd,
}: {
  shift: Shift;
  canWrite: boolean;
  onAdd: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const expenses = useShiftCashExpenses(shift.id);
  return (
    <TabBody
      isPending={expenses.isPending}
      error={expenses.error}
      isEmpty={(expenses.data ?? []).length === 0}
      emptyTitle={t("shifts.detail.cashExpensesEmpty")}
      icon={<Coins />}
      action={
        canWrite ? (
          <Button variant="outline" size="sm" onClick={onAdd}>
            <Plus aria-hidden="true" />
            {t("shifts.records.cashExpenseTitle")}
          </Button>
        ) : null
      }
    >
      <SimpleTable
        caption={t("shifts.detail.cashExpensesCaption")}
        head={
          <tr>
            <th scope="col" className={thClass}>
              {t("shifts.fields.staff")}
            </th>
            <th scope="col" className={thClass}>
              {t("shifts.fields.occurredAt")}
            </th>
            <th scope="col" className={thClass}>
              {t("shifts.records.description")}
            </th>
            <th scope="col" className={thClass}>
              {t("shifts.fields.expenseDocNo")}
            </th>
            <th scope="col" className={thRightClass}>
              {t("shifts.records.amount")}
            </th>
          </tr>
        }
      >
        {(expenses.data ?? []).map((expense) => (
          <tr key={expense.id} className="border-t align-top">
            <td className={tdClass}>{expense.staffName}</td>
            <td className={`${tdClass} tabular`}>{formatDateTime(expense.occurredAt)}</td>
            <td className={tdClass}>{expense.description}</td>
            <td className={tdClass}>
              <Link
                to="/giderler/$id"
                params={{ id: expense.expenseId }}
                className="underline underline-offset-4"
              >
                {expense.expenseDocNo ?? t("shifts.fields.expenseLink")}
              </Link>
            </td>
            <td className={tdRightClass}>
              <MoneyText value={expense.amount} />
            </td>
          </tr>
        ))}
      </SimpleTable>
    </TabBody>
  );
}

function CreditSalesTab({
  shift,
  canWrite,
  onAdd,
  onCancel,
}: {
  shift: Shift;
  canWrite: boolean;
  onAdd: () => void;
  onCancel: (target: CancelTarget) => void;
}): ReactElement {
  const { t } = useTranslation();
  const sales = useShiftCreditSales(shift.id);
  return (
    <TabBody
      isPending={sales.isPending}
      error={sales.error}
      isEmpty={(sales.data ?? []).length === 0}
      emptyTitle={t("shifts.detail.creditSalesEmpty")}
      icon={<Receipt />}
      action={
        canWrite ? (
          <Button variant="outline" size="sm" onClick={onAdd}>
            <Plus aria-hidden="true" />
            {t("shifts.records.creditSaleTitle")}
          </Button>
        ) : null
      }
    >
      <SimpleTable
        caption={t("shifts.detail.creditSalesCaption")}
        head={
          <tr>
            <th scope="col" className={thClass}>
              {t("shifts.records.slipNo")}
            </th>
            <th scope="col" className={thClass}>
              {t("shifts.records.creditSaleKind")}
            </th>
            <th scope="col" className={thClass}>
              {t("shifts.records.counterparty")}
            </th>
            <th scope="col" className={thClass}>
              {t("shifts.records.plate")}
            </th>
            <th scope="col" className={thRightClass}>
              {t("shifts.records.litres")}
            </th>
            <th scope="col" className={thRightClass}>
              {t("shifts.records.amount")}
            </th>
            <th scope="col" className={thClass}>
              {t("common.status")}
            </th>
            <th scope="col" className={thClass}>
              {t("common.actions")}
            </th>
          </tr>
        }
      >
        {(sales.data ?? []).map((sale) => (
          <tr key={sale.id} className="border-t align-top">
            <td className={`${tdClass} font-mono`}>{sale.slipNo}</td>
            <td className={tdClass}>{SHIFT_CREDIT_SALE_KIND_LABELS[sale.kind]}</td>
            <td className={tdClass}>{sale.counterpartyName}</td>
            <td className={tdClass}>{sale.plate ?? "—"}</td>
            <td className={tdRightClass}>{formatLitres(sale.litres)}</td>
            <td className={tdRightClass}>
              <MoneyText value={sale.amount} />
            </td>
            <td className={tdClass}>
              {sale.cancelledAt ? (
                <StatusBadge tone="danger">{t("shifts.records.cancelledBadge")}</StatusBadge>
              ) : (
                <StatusBadge tone="success">{t("shifts.records.activeBadge")}</StatusBadge>
              )}
              {sale.creditCheck?.overridden ? (
                <>
                  {" "}
                  <StatusBadge tone="warning">{t("shifts.records.overriddenBadge")}</StatusBadge>
                </>
              ) : null}
              {/* Prompt 17: elle filo fişinde kural ihlali yetkili onayıyla geçildiyse (karar fişe dondurulur). */}
              {sale.fleetAuthorization?.verdict === "OVERRIDDEN" ? (
                <>
                  {" "}
                  <StatusBadge tone="warning">{t("shifts.records.fleetOverriddenBadge")}</StatusBadge>
                </>
              ) : null}
            </td>
            <td className={tdClass}>
              {canWrite && !sale.cancelledAt ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onCancel({ kind: "creditSale", recordId: sale.id })}
                >
                  {t("shifts.records.cancelRecord")}
                </Button>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </td>
          </tr>
        ))}
      </SimpleTable>
    </TabBody>
  );
}

function TestDrawsTab({
  shift,
  canWrite,
  onAdd,
}: {
  shift: Shift;
  canWrite: boolean;
  onAdd: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const draws = useShiftTestDraws(shift.id);
  return (
    <TabBody
      isPending={draws.isPending}
      error={draws.error}
      isEmpty={(draws.data ?? []).length === 0}
      emptyTitle={t("shifts.detail.testDrawsEmpty")}
      icon={<Droplet />}
      action={
        canWrite ? (
          <Button variant="outline" size="sm" onClick={onAdd}>
            <Plus aria-hidden="true" />
            {t("shifts.records.testDrawTitle")}
          </Button>
        ) : null
      }
    >
      <SimpleTable
        caption={t("shifts.detail.testDrawsCaption")}
        head={
          <tr>
            <th scope="col" className={thClass}>
              {t("shifts.records.nozzle")}
            </th>
            <th scope="col" className={thClass}>
              {t("shifts.fields.occurredAt")}
            </th>
            <th scope="col" className={thRightClass}>
              {t("shifts.records.litres")}
            </th>
            <th scope="col" className={thClass}>
              {t("shifts.records.returnedToTank")}
            </th>
          </tr>
        }
      >
        {(draws.data ?? []).map((draw) => (
          <tr key={draw.transactionId} className="border-t align-top">
            <td className={tdClass}>{draw.nozzleLabel}</td>
            <td className={`${tdClass} tabular`}>{formatDateTime(draw.occurredAt)}</td>
            <td className={tdRightClass}>{formatLitres(draw.litres)}</td>
            <td className={tdClass}>{draw.returnedToTank ? t("common.yes") : t("common.no")}</td>
          </tr>
        ))}
      </SimpleTable>
    </TabBody>
  );
}

function TabBody({
  isPending,
  error,
  isEmpty,
  emptyTitle,
  icon,
  action,
  children,
}: {
  isPending: boolean;
  error: unknown;
  isEmpty: boolean;
  emptyTitle: string;
  icon: ReactElement;
  action: ReactElement | null;
  children: ReactElement;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <div className="grid gap-3">
      {action ? <div className="flex flex-wrap gap-2">{action}</div> : null}
      {error ? (
        <Alert tone="danger">{errorMessage(error)}</Alert>
      ) : isPending ? (
        <SkeletonRows rows={4} />
      ) : isEmpty ? (
        <EmptyState
          headingLevel={2}
          icon={icon}
          title={emptyTitle}
          description={t("shifts.detail.emptyDescription")}
        />
      ) : (
        children
      )}
    </div>
  );
}
